import cssContent from './styles/visual-editor.css?inline';
import { visualEditorApi } from './services/visualEditorApi.ts';
import { VisualEditorDock } from './components/VisualEditorDock.ts';
import { HoverInspector } from './components/HoverInspector.ts';
import { VisualEditorDrawer, CONTAINER_CHILD_TYPES } from './components/VisualEditorDrawer.ts';
import type { VisualEditorPage } from './types.ts';

function injectStyles(): void {
  if (typeof document === 'undefined') return;
  if (document.getElementById('loumars-visual-editor-styles')) return;
  const style = document.createElement('style');
  style.id = 'loumars-visual-editor-styles';
  style.textContent = cssContent;
  document.head.appendChild(style);
}

export class LoumarsVisualEditorApp {
  private dock: VisualEditorDock = new VisualEditorDock();
  private inspector: HoverInspector = new HoverInspector();
  private drawer: VisualEditorDrawer = new VisualEditorDrawer();
  private page: VisualEditorPage | null = null;
  private rootElement: HTMLElement | null = null;
  private isStarted: boolean = false;

  public async start(): Promise<void> {
    if (this.isStarted) return;
    const pathLower =
      typeof window !== 'undefined'
        ? window.location.pathname.toLowerCase().replace(/\/+$/, '')
        : '';
    const authSegments = ['/login', '/register', '/logoff', '/forgotpassword', '/resetpassword'];
    if (authSegments.some((seg) => pathLower.endsWith(seg)) || pathLower.includes('/admin')) {
      return;
    }

    try {
      const config = await visualEditorApi.fetchConfig();
      const urlParams =
        typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null;
      const isExplicitEditRequest =
        urlParams?.get('edit') === 'true' ||
        (typeof window !== 'undefined' &&
          (window.location.hash === '#edit' ||
            window.sessionStorage?.getItem('loumars_visual_editor_active') === 'true'));

      if (!config.canEdit) {
        if (isExplicitEditRequest && !config.isAuthenticated && typeof window !== 'undefined') {
          const currentUrl =
            window.location.pathname + window.location.search + window.location.hash;
          const loginPath = config.tenantPrefix ? `/${config.tenantPrefix}/Login` : '/Login';
          window.location.href = `${loginPath}?returnUrl=${encodeURIComponent(currentUrl)}`;
          return;
        }
        return; // User is not authorized to edit; exit cleanly
      }

      this.isStarted = true;
      injectStyles();
      this.ensureRootElement();
      await this.loadPage();
      this.mountComponents();

      if (isExplicitEditRequest) {
        this.dock.setActive(true);
        this.inspector.setEnabled(true);
      }
    } catch (err) {
      console.warn('[VisualEditor] Initialization skipped:', err);
    }
  }

  private ensureRootElement(): void {
    let root = document.getElementById('loumars-visual-editor-root');
    if (!root) {
      root = document.createElement('div');
      root.id = 'loumars-visual-editor-root';
      root.className = 'loumars-visual-editor-root';
      root.setAttribute('data-id', 'loumars-visual-editor-root');
      document.body.appendChild(root);
    } else {
      root.innerHTML = '';
    }
    this.rootElement = root;
  }

  private async loadPage(): Promise<void> {
    const currentPath = window.location.pathname;
    const bodyContentItemId =
      document.body?.dataset?.contentItemId ||
      document.getElementById('main-content')?.getAttribute('data-content-item-id') ||
      undefined;

    try {
      this.page = await visualEditorApi.getPage(currentPath, bodyContentItemId);
    } catch {
      if (bodyContentItemId) {
        try {
          this.page = await visualEditorApi.getPage(currentPath);
        } catch {
          this.page = null;
        }
      } else {
        this.page = null;
      }
    }
  }

  private mountComponents(): void {
    if (!this.rootElement || !this.page) return;

    // 1. Mount Dock
    this.dock.render(this.rootElement, this.page.widgets.length, (active) => {
      this.inspector.setEnabled(active);
      if (!active) {
        this.drawer.close();
      }
    });

    // 2. Init Inspector
    this.inspector.setPageId(this.page.contentItemId);
    this.inspector.init({
      onSelectWidget: (widgetId) => {
        if (!this.page) return;
        this.drawer.open(this.page, widgetId, 'editor');
      },
      onMoveUp: (widgetId) => this.reorderRelative(widgetId, -1),
      onMoveDown: (widgetId) => this.reorderRelative(widgetId, 1),
      onDelete: (widgetId, widgetName) => {
        const name = widgetName || 'Widget';
        this.drawer.promptDeleteConfirm(widgetId, name, async () => {
          await this.deleteWidget(widgetId);
        });
      },
      onInsertBetween: async (targetWidgetId, position, isSubItem) => {
        if (!this.page) return;
        if (isSubItem) {
          const parent = this.drawer.findParentWidget(targetWidgetId);
          if (parent && parent.subItems) {
            const subIdx = parent.subItems.findIndex((s) => s.contentItemId === targetWidgetId);
            const insertIndex =
              subIdx !== -1 ? (position === 'before' ? subIdx : subIdx + 1) : undefined;
            const containerConfig = CONTAINER_CHILD_TYPES[parent.contentType];
            const isMenu =
              parent.contentType === 'Nav' ||
              parent.contentType === 'Navbar' ||
              parent.contentType === 'Footer' ||
              parent.contentType === 'Menu';
            const childType =
              containerConfig?.childType || (isMenu ? 'LinkMenuItem' : 'FeatureItem');

            try {
              const newWidget = await visualEditorApi.addWidget(
                this.page.contentItemId,
                childType,
                insertIndex,
                parent.contentItemId
              );
              await this.reloadPageData();
              this.drawer.showToast(`Inserted ${newWidget.contentType}`);
              this.drawer.open(this.page, newWidget.contentItemId, 'editor');
            } catch (err: unknown) {
              const msg = err instanceof Error ? err.message : String(err);
              console.error('[VisualEditor] Failed to insert sub-item:', msg);
              this.drawer.showToast('Failed to insert item');
            }
            return;
          }
        }
        this.drawer.openWithInsertionTarget(targetWidgetId, position);
      },
    });

    // 3. Mount Drawer
    this.drawer.render(this.rootElement, {
      onOpen: () => {
        this.dock.setDrawerOpen(true);
      },
      onClose: () => {
        this.dock.setDrawerOpen(false);
        this.inspector.hide();
      },
      onWidgetUpdated: async (widgetId, data) => {
        if (!this.page) return;
        await visualEditorApi.updateWidget(this.page.contentItemId, widgetId, data);
        await this.reloadPageData();
      },
      onWidgetReordered: async (widgetIds, parentWidgetContentItemId) => {
        if (!this.page) return;
        await visualEditorApi.reorderWidgets(
          this.page.contentItemId,
          widgetIds,
          parentWidgetContentItemId
        );
        await this.reloadPageData();
        this.drawer.showToast('Widgets Reordered');
      },
      onWidgetAdded: async (widgetType, parentWidgetContentItemId, insertIndex) => {
        if (!this.page) return;
        try {
          const newWidget = await visualEditorApi.addWidget(
            this.page.contentItemId,
            widgetType,
            insertIndex,
            parentWidgetContentItemId
          );
          await this.reloadPageData();
          this.drawer.showToast(`Added ${newWidget.contentType} widget`);
          this.drawer.open(this.page, newWidget.contentItemId, 'editor');
        } catch (err: unknown) {
          const msg = err instanceof Error ? err.message : String(err);
          console.error('[VisualEditor] Failed to add widget:', msg);
          this.drawer.showToast('Failed to add widget');
        }
      },
      onWidgetDeleted: async (widgetId, parentWidgetContentItemId) => {
        await this.deleteWidget(widgetId, parentWidgetContentItemId);
      },
      onPageSettingsUpdated: async (settings) => {
        if (!this.page) return;
        await visualEditorApi.updatePageSettings(
          this.page.contentItemId,
          settings.displayText,
          settings.pageTitle,
          settings.metaDescription,
          settings.path
        );
        await this.reloadPageData();
        this.drawer.showToast('Page settings updated');
      },
      onPublish: async () => {
        if (!this.page) return;
        await visualEditorApi.publishPage(this.page.contentItemId);
      },
      onSaveDraft: async () => {
        if (!this.page) return;
        await visualEditorApi.saveDraft(this.page.contentItemId);
      },
    });
  }

  private async reorderRelative(widgetId: string, direction: number): Promise<void> {
    if (!this.page) return;
    const idx = this.page.widgets.findIndex((w) => w.contentItemId === widgetId);
    if (idx === -1) return;

    const targetIdx = idx + direction;
    if (targetIdx < 0 || targetIdx >= this.page.widgets.length) return;

    const ids = this.page.widgets.map((w) => w.contentItemId);
    const temp = ids[idx];
    ids[idx] = ids[targetIdx];
    ids[targetIdx] = temp;

    await visualEditorApi.reorderWidgets(this.page.contentItemId, ids);
    await this.reloadPageData();
    this.drawer.showToast('Widgets Reordered');
  }

  private async deleteWidget(
    widgetId: string,
    parentWidgetContentItemId?: string | null
  ): Promise<void> {
    if (!this.page) return;
    try {
      await visualEditorApi.deleteWidget(
        this.page.contentItemId,
        widgetId,
        parentWidgetContentItemId
      );
      await this.reloadPageData();
      this.drawer.showToast('Widget removed');
      if (this.page) {
        this.drawer.open(this.page, undefined, 'tree');
      }

      const domEl = document.querySelector(`[data-content-item-id="${widgetId}"]`);
      domEl?.remove();
    } catch (err) {
      console.error('[VisualEditor] Failed to delete widget:', err);
      this.drawer.showToast('Failed to delete widget');
    }
  }

  private async reloadPageData(): Promise<void> {
    await this.loadPage();
    if (this.page) {
      this.dock.updateWidgetCount(this.page.widgets.length);
    }
  }

  public openDrawer(
    widgetId?: string,
    tab: import('./components/VisualEditorDrawer.ts').DrawerTab = 'editor'
  ): void {
    if (!this.page) return;
    this.drawer.open(this.page, widgetId, tab);
  }

  public closeDrawer(): void {
    this.drawer.close();
  }

  public toggleEditMode(active?: boolean): void {
    const next = active !== undefined ? active : !this.dock.getIsActive();
    this.dock.setActive(next);
  }

  public getPage(): VisualEditorPage | null {
    return this.page;
  }

  public getDrawer(): VisualEditorDrawer {
    return this.drawer;
  }

  public getDock(): VisualEditorDock {
    return this.dock;
  }

  public getInspector(): HoverInspector {
    return this.inspector;
  }
}

export const visualEditor = new LoumarsVisualEditorApp();

if (typeof window !== 'undefined') {
  (window as unknown as Record<string, unknown>).loumarsVisualEditor = visualEditor;
}

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => visualEditor.start());
  } else {
    visualEditor.start();
  }
}
